/**
 * DravonixHero - Main Wrapper Component
 * ======================================
 * Orchestrates the cinematic loading → hero transition
 * 
 * Phases:
 * 1. 'loading' - Fullscreen loading overlay with progress ring
 * 2. 'transition' - Ring expansion, particle dissolve
 * 3. 'hero' - Main hero content animation
 * 4. 'complete' - Final state with continuous animations
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { gsap } from 'gsap';
import LoadingScreen from './LoadingScreen';
import HeroSection from './HeroSection';
import './styles.css';

const DravonixHero = () => {
  const [phase, setPhase] = useState('loading'); // 'loading' | 'transition' | 'hero' | 'complete'
  const [canSkip, setCanSkip] = useState(true);
  const [showReplay, setShowReplay] = useState(false);
  const masterTimeline = useRef(null);
  const containerRef = useRef(null);

  // Animation plays on every page load/refresh
  // No session storage check - always show the cinematic experience

  // Skip animation handler
  const handleSkip = useCallback(() => {
    if (masterTimeline.current) {
      masterTimeline.current.progress(1);
    }
    setPhase('complete');
    setCanSkip(false);
    setShowReplay(true);
  }, []);

  // Replay animation handler
  const handleReplay = useCallback(() => {
    setPhase('loading');
    setCanSkip(true);
    setShowReplay(false);
  }, []);

  // Phase transition handler
  const handlePhaseComplete = useCallback((completedPhase) => {
    if (completedPhase === 'loading') {
      setPhase('transition');
    } else if (completedPhase === 'transition') {
      setPhase('hero');
    } else if (completedPhase === 'hero') {
      setPhase('complete');
      setCanSkip(false);
      setShowReplay(true);
    }
  }, []);

  // Register master timeline
  const registerTimeline = useCallback((timeline) => {
    masterTimeline.current = timeline;
  }, []);

  return (
    <div className="dravonix-hero-container" ref={containerRef}>
      {/* Loading Screen */}
      {(phase === 'loading' || phase === 'transition') && (
        <LoadingScreen
          phase={phase}
          onPhaseComplete={handlePhaseComplete}
          registerTimeline={registerTimeline}
        />
      )}

      {/* Hero Section */}
      <HeroSection
        phase={phase}
        onPhaseComplete={handlePhaseComplete}
        isVisible={phase !== 'loading'}
      />

      {/* Skip Button */}
      {canSkip && phase !== 'complete' && (
        <button className="skip-button" onClick={handleSkip} aria-label="Skip animation">
          Skip
          <span className="skip-icon">→</span>
        </button>
      )}

      {/* Replay Button */}
      {showReplay && (
        <button className="replay-button" onClick={handleReplay} aria-label="Replay animation">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 4v6h6M23 20v-6h-6" />
            <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default DravonixHero;
